/**
 * HiroMatchLogo — the official brand mark used across the app.
 *
 * Single source of truth, so a future redesign only touches this file.
 * Already inlined as SVG in `AppLayout`, `OnboardingPage` and
 * `AuthMarketingPanel` for legacy reasons; new surfaces (admin chrome,
 * empty states, marketing pages) should import this component.
 */
export function HiroMatchLogo({
  className = 'h-7 w-7',
  ariaLabel = 'HiroMatch',
}: {
  className?: string;
  ariaLabel?: string;
}): JSX.Element {
  // Stable id avoids clipPath collisions if the logo is rendered twice
  // on the same page (e.g. login marketing panel + header).
  const clipId = `hm-logo-clip-${useStableId()}`;
  return (
    <svg
      viewBox="0 0 360 320"
      xmlns="http://www.w3.org/2000/svg"
      className={`${className} shrink-0`}
      aria-label={ariaLabel}
      role="img"
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx="225" cy="160" r="90" />
        </clipPath>
      </defs>
      <circle cx="135" cy="160" r="88" fill="#7C3AED" opacity="0.9" />
      <circle cx="225" cy="160" r="90" fill="#C4B5FD" opacity="0.82" />
      <circle cx="135" cy="160" r="88" fill="#9333EA" opacity="0.3" clipPath={`url(#${clipId})`} />
    </svg>
  );
}

/**
 * Tiny module-scoped counter so each <HiroMatchLogo> on the page gets
 * a unique clipPath id without resorting to React's `useId` (which would
 * make the component itself a hook callsite — overkill for a static mark).
 */
let _logoCounter = 0;
function useStableId(): number {
  // We don't actually need React reactivity here; a per-render id is fine
  // because the clipPath id is read by the same SVG instance that owns it.
  return ++_logoCounter;
}
